import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only';
import { reportError } from '@gef-ui/error-handler/actions';
import invariant from 'invariant';
import T from "ramda/es/T";
import always from "ramda/es/always";
import clone from "ramda/es/clone";
import cond from "ramda/es/cond";
import defaultTo from "ramda/es/defaultTo";
import identity from "ramda/es/identity";
import isNil from "ramda/es/isNil";
import when from "ramda/es/when";
import { fetch as globalFetch } from 'whatwg-fetch';
import { CALL_API, CONTENT_TYPE, NOT_PROVIDED } from '../../constants';
import { callActionsWithArgs, createActionMeta, includeHeader, includeHeaderFile, logApiActionDebug, logApiError, resolveValue } from '../utils';
const abortMutableReferenceHolder = {};
export const parseContent = (response)=>cond([
        [
            isNil,
            ()=>Promise.resolve()
        ],
        [
            includeHeader(CONTENT_TYPE.APPLICATION_JSON),
            ()=>response.json()
        ],
        [
            includeHeader(CONTENT_TYPE.MULTIPART_FORM_DATA),
            ()=>response.formData()
        ],
        [
            includeHeader('text'),
            ()=>response.text()
        ],
        [
            includeHeaderFile,
            ()=>response.blob()
        ],
        [
            T,
            ()=>Promise.resolve()
        ]
    ])(response.headers.get('Content-Type'));
const defaultOkResponseResolver = (response)=>response.ok ? response : Promise.reject(response);
const okResponseResolver = when(isNil, always(defaultOkResponseResolver));
const cancelPrevious = ({ operationUuid }, abortController)=>{
    invariant(operationUuid && operationUuid !== NOT_PROVIDED, 'You need to specify unique operationUuid (defaulted by actionName) to get work aborting');
    const previousAbortController = abortMutableReferenceHolder[operationUuid];
    if (previousAbortController) {
        previousAbortController.abort();
    }
    abortMutableReferenceHolder[operationUuid] = abortController;
};
const addOriginalAction = (data = {}, originalAction)=>{
    // note: clone will not break object type > exception will stay as exception.
    // for some cases we need to provide originalAction in error as enhancement
    // of original object without loosing information about its original type
    // so {...data, originalAction} cannot be used, because it will break exceptions
    const dataClone = clone(data);
    dataClone.originalAction = originalAction;
    return dataClone;
};
/**
 *
 * api middleware
 *
 * @documented
 * @category api
 *
 * @param {String} [defaultCredentials=same-origin] - default credentials configuration
 * @param {String} [frontendUrl] - frontend base URL
 * @param {Boolean} [customLogoutResolver] - custom logout resolver
 * @param {Function} [contentParser=parseContent] - custom content parser default to parseContent
 * @return {undefined} nothing
 *
 * @example
 *
 * Description of action:
 * action = {
 * 	[CALL_API]: {
 * 		endpoint: {String|Function(state)},
 * 		actions: {
 * 			onRequest: {Function(null, meta)},
 * 			onSuccess: {Function({json, headers}, meta)},
 * 			onError: {Function(response, meta)},
 * 			onAbort: {Function(DOMException, meta)},
 * 		}
 * 		body: {Object|Function(state)},
 * 		method: {String},
 * 		credentials: {String} [credentials={'same-origin'}],
 *		headers: {Object|Function(state)} [headers={see default}],
 *		responseManipulation: {Function(response)} response manipulation function,
 *		errorManipulation: {Function(error, endpoint, params)} error manipulation function,
 *		actionName: {String} action name for logger,
 *		operationUuid: {String} [runInBatch=actionName] unique id for whole operation, necessary for aborting.
 *		abortPreviousCall: {Boolean} [abortPreviousCall=false] abort previous call if pending
 *		runInBatch: {Boolean} [runInBatch=false] run actions with batch actions at once,
 *		customOkResponseResolver: {Function},
 *		notReportError: {Boolean} not report error option
 * 	},
 * }
 */ const apiMiddleware = ({ defaultCredentials = 'same-origin', frontendUrl, customLogoutResolver, fetchImpl = globalFetch, alterAction = identity, contentParser = parseContent } = {})=>({ getState, dispatch })=>(next)=>async (action)=>{
                let callAPI = action[CALL_API];
                if (!callAPI) {
                    return next(action);
                }
                callAPI = alterAction(callAPI, getState);
                const state = getState();
                const resolveValueWithState = resolveValue(state);
                const callNextActions = callActionsWithArgs(dispatch, callAPI.runInBatch);
                const endpoint = await resolveValueWithState(callAPI.endpoint);
                const abortController = new AbortController();
                if (callAPI.abortPreviousCall) {
                    cancelPrevious(callAPI, abortController);
                }
                const params = {
                    headers: await resolveValueWithState(callAPI.headers),
                    method: callAPI.method,
                    body: await resolveValueWithState(callAPI.body),
                    credentials: defaultTo(defaultCredentials, callAPI.credentials),
                    signal: abortController.signal
                };
                const resolveOkResponse = okResponseResolver(callAPI.customOkResponseResolver);
                const { onRequest, onSuccess, onError, onAbort } = callAPI.actions;
                logApiActionDebug(callAPI, params, endpoint);
                const meta = createActionMeta(callAPI.meta);
                const actionMeta = {
                    ...meta,
                    abortController
                };
                callNextActions(onRequest, [
                    null,
                    actionMeta
                ]);
                return fetchImpl(endpoint, params).then(resolveOkResponse).then(async (response)=>{
                    let parsedResponse;
                    try {
                        const body = await contentParser(response);
                        const { headers, status } = response;
                        const responseManipulation = callAPI.responseManipulation || identity;
                        parsedResponse = responseManipulation({
                            body,
                            headers,
                            status
                        });
                    } catch (err) {
                        logApiError(err, 'Api');
                        return Promise.reject(addOriginalAction(parsedResponse, callAPI));
                    }
                    return parsedResponse;
                }).catch(async (response)=>{
                    if (response instanceof DOMException && response.name === 'AbortError') {
                        callNextActions(onAbort, [
                            response,
                            actionMeta
                        ]);
                        return Promise.reject(addOriginalAction(response, callAPI));
                    }
                    let error;
                    try {
                        error = {
                            status: response.status,
                            statusText: response.statusText,
                            responseHeaders: response.headers,
                            parsedContent: await contentParser(response),
                            meta
                        };
                        const errorManipulation = callAPI.errorManipulation || identity;
                        error = errorManipulation(error, endpoint, params);
                    } catch (_err) {
                        error = {
                            status: response.status || 500,
                            errorCode: 'internal-server-error',
                            message: 'Unexpected error occurred',
                            responseHeaders: response.headers,
                            meta
                        };
                    }
                    if (!callAPI.notReportError) {
                        dispatch(reportError(error));
                    }
                    callNextActions(onError, [
                        {
                            failure: error,
                            frontendUrl,
                            customLogoutResolver
                        },
                        actionMeta
                    ]);
                    return Promise.reject(addOriginalAction(error, callAPI));
                }).then((parsedResponse)=>{
                    callNextActions(onSuccess, [
                        parsedResponse,
                        actionMeta
                    ]);
                    return Promise.resolve(addOriginalAction(parsedResponse, callAPI));
                }).catch((err)=>{
                    return Promise.reject(addOriginalAction(err, callAPI));
                });
            };
export default apiMiddleware;
